/** TAG_CATEGORIES / LOCATION_TAG_CATEGORIES read-only display: the tag-category set
 *  that applies to this operator + each of its category codes, marked created or not —
 *  and a link to the operator's default tag categories, which hold the set's
 *  location-sharing association rows.
 */
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { PreflightItem } from '@/api/preflight';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { operatorDefaultsHref, tagCategoryCodeActions, type TenantCtx } from '../tenantLinks';

interface TagSet {
  code: string;
  name: string;
}
interface Cat {
  code: string;
  created: boolean;
  association_expected: boolean;
  association_applied: boolean;
}

export function TagCategoriesDisplay({
  item,
  topId,
  toCode,
}: {
  item: PreflightItem;
  topId: number;
  toCode: string;
}) {
  const ctx: TenantCtx = { id: topId, code: toCode };
  const detail = item.detail;
  const set =
    detail.tag_set && typeof detail.tag_set === 'object' ? (detail.tag_set as TagSet) : null;
  const cats: Cat[] = Array.isArray(detail.categories)
    ? (detail.categories as unknown[]).map((c) => {
        const o = c as Record<string, unknown>;
        return {
          code: String(o.code ?? ''),
          created: o.created === true,
          association_expected: o.association_expected === true,
          association_applied: o.association_applied === true,
        };
      })
    : [];

  if (!set) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        No tag-category set resolved for this operator.
      </Typography>
    );
  }

  const createdCount = cats.filter((c) => c.created).length;
  const assocExpected = cats.filter((c) => c.association_expected);
  const assocApplied = assocExpected.filter((c) => c.association_applied).length;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Applied set: <strong>{set.name}</strong> ({set.code}) — {createdCount}/{cats.length} categories
        created
        {assocExpected.length > 0 &&
          ` · ${assocApplied}/${assocExpected.length} location-sharing associations applied`}
        {' · '}
        <Link component={RouterLink} to={operatorDefaultsHref(topId, 'tag-categories')}>
          Operator’s default tag categories
        </Link>
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {cats.map((c) => {
          // Location/fleet categories carry an operator-default association row; flag it as
          // pending (warning) when the category exists but the flags aren't in sync yet.
          const pendingAssoc = c.association_expected && c.created && !c.association_applied;
          return (
            // The payload carries the category CODE only, so there is no
            // /tag-categories/<id> to open — the operator's list and the tags
            // in the category are as close as a link can get.
            <ValueActions key={c.code} chip actions={tagCategoryCodeActions(c.code, ctx)}>
              <Chip
                size="small"
                label={c.association_expected ? `${c.code} · loc ${c.association_applied ? '✓' : '…'}` : c.code}
                color={c.created ? (pendingAssoc ? 'warning' : 'success') : 'default'}
                variant={c.created ? 'filled' : 'outlined'}
              />
            </ValueActions>
          );
        })}
      </Stack>
    </Box>
  );
}
